/**
 * useSurveyObservations — manages observations for a specific capture.
 *
 * @remarks
 * Loads observations by captureId on mount.
 * Exposes create, confirm, remove actions.
 * Only fires when captureId is non-null.
 */

import { useCallback, useEffect, useState } from 'react';
import { getSurveyApiError, surveyApi } from '../services/survey-api.service';
import type {
  CreateObservationPayload,
  SurveyObservation,
  UpdateObservationPayload,
} from '../types/survey.types';

interface UseSurveyObservationsResult {
  observations: SurveyObservation[];
  isLoading: boolean;
  error: string | null;
  create: (payload: CreateObservationPayload) => Promise<void>;
  update: (id: string, payload: UpdateObservationPayload) => Promise<void>;
  confirm: (id: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export function useSurveyObservations(captureId: string | null): UseSurveyObservationsResult {
  const [observations, setObservations] = useState<SurveyObservation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (id: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await surveyApi.listObservationsByCapture(id);
      setObservations(data);
    } catch (err) {
      setError(getSurveyApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect((): void => {
    if (!captureId) {
      setObservations([]);
      return;
    }
    void reload(captureId);
  }, [captureId, reload]);

  const create = useCallback(async (payload: CreateObservationPayload): Promise<void> => {
    const obs = await surveyApi.createObservation(payload);
    setObservations(prev => [obs, ...prev]);
  }, []);

  const update = useCallback(
    async (id: string, payload: UpdateObservationPayload): Promise<void> => {
      const updated = await surveyApi.updateObservation(id, payload);
      setObservations(prev => prev.map(o => (o.id === id ? updated : o)));
    },
    []
  );

  const confirm = useCallback(async (id: string): Promise<void> => {
    const updated = await surveyApi.confirmObservation(id);
    setObservations(prev => prev.map(o => (o.id === id ? updated : o)));
  }, []);

  const remove = useCallback(async (id: string): Promise<void> => {
    await surveyApi.deleteObservation(id);
    setObservations(prev => prev.filter(o => o.id !== id));
  }, []);

  return { observations, isLoading, error, create, update, confirm, remove };
}
